import { useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { errorCode, errorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

export default function UserProfile() {
  const { user, setUser } = useAuth();
  const loading = !user;
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    address: user?.address || "",
    city: user?.city || "",
    state: user?.state || "",
    zip: user?.zip || "",
    phone: user?.phone || "",
    email: user?.email || "",
  });

  const [passwords, setPasswords] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setMessage("");
    setError("");
  };

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
    setMessage("");
    setError("");
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const updated = await api.me.update({
        display_name: form.display_name,
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
        phone: form.phone,
      });
      setUser(updated);
      setForm({
        display_name: updated.display_name || "",
        address: updated.address || "",
        city: updated.city || "",
        state: updated.state || "",
        zip: updated.zip || "",
        phone: updated.phone || "",
        email: updated.email || "",
      });
      setMessage("Profile updated successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(errorMessage(err, "Failed to save profile changes"));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!passwords.current_password) {
      setError("Please enter your current password");
      return;
    }
    if (!passwords.new_password) {
      setError("Please enter a new password");
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      setError("New passwords do not match");
      return;
    }
    if (passwords.new_password.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }

    setSaving(true);
    try {
      await api.auth.changePassword(passwords.current_password, passwords.new_password);
      setMessage("Password changed successfully");
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
      setShowPasswordSection(false);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(errorCode(err) === "INVALID_PASSWORD" ? "Current password is incorrect" : errorMessage(err, "Failed to change password"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-8">
        <div className="max-w-2xl mx-auto px-4 flex justify-center items-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <Link
          to={createPageUrl("Home")}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Profile Settings</h1>

          {message && (
            <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg mb-6 text-green-700">
              <CheckCircle className="h-5 w-5" />
              {message}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6 text-red-700">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}

          {/* Profile Information */}
          <form onSubmit={handleSaveProfile} className="space-y-6 mb-8">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="display_name" className="text-sm font-medium text-gray-700">
                    Display Name
                  </Label>
                  <Input
                    id="display_name"
                    value={form.display_name}
                    onChange={(e) => handleInputChange("display_name", e.target.value)}
                    className="mt-1"
                    placeholder="Enter your display name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    disabled
                    className="mt-1 bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="mt-1"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="address" className="text-sm font-medium text-gray-700">
                      Address
                    </Label>
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      className="mt-1"
                      placeholder="Street address"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                      City
                    </Label>
                    <Input
                      id="city"
                      value={form.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      className="mt-1"
                      placeholder="City"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state" className="text-sm font-medium text-gray-700">
                      State
                    </Label>
                    <Input
                      id="state"
                      value={form.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      className="mt-1"
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <Label htmlFor="zip" className="text-sm font-medium text-gray-700">
                      ZIP Code
                    </Label>
                    <Input
                      id="zip"
                      value={form.zip}
                      onChange={(e) => handleInputChange("zip", e.target.value)}
                      className="mt-1"
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Profile Changes
            </Button>
          </form>

          {/* Password Change Section */}
          <div className="border-t border-gray-200 pt-8">
            {!showPasswordSection ? (
              <button
                onClick={() => setShowPasswordSection(true)}
                className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
              >
                Change Password
              </button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>

                <div>
                  <Label htmlFor="current_password" className="text-sm font-medium text-gray-700">
                    Current Password
                  </Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwords.current_password}
                    onChange={(e) => handlePasswordChange("current_password", e.target.value)}
                    className="mt-1"
                    placeholder="Enter your current password"
                  />
                </div>

                <div>
                  <Label htmlFor="new_password" className="text-sm font-medium text-gray-700">
                    New Password
                  </Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwords.new_password}
                    onChange={(e) => handlePasswordChange("new_password", e.target.value)}
                    className="mt-1"
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password" className="text-sm font-medium text-gray-700">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwords.confirm_password}
                    onChange={(e) => handlePasswordChange("confirm_password", e.target.value)}
                    className="mt-1"
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Update Password
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswords({
                        current_password: "",
                        new_password: "",
                        confirm_password: "",
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}